/**
 * AI Chat Component - Handles AI interactions and streaming responses
 * See DOCUMENTATION.md for complete slide creation flow documentation
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Fade, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/UserContext';
import { usePresentationChats } from '../../../shared/contexts';
import { presentationRepository } from '../../../shared/services/data';
import { analytics } from '../../../shared/services/analytics';
import { ChatMessageCard, ChatComposer, ChatHeaderBar, HistorySidebarPanel, WelcomeCards, SandboxRedemptionBanner, OrgSeatBanner } from './components/UIComponents';
import UserQuestionCard from './components/UserQuestionCard';
import ProposeEditsCard from './components/ProposeEditsCard';
import MessageRenderer from './components/MessageRenderer';
import { useChatComposer, useStreamingSession } from './hooks';
import { DEFAULT_MODEL, MODEL_STORAGE_KEY } from './constants';
import { ChatSidebar, ChatMessages, ChatComposerWrapper, getChatRadii, getChatBorders } from './AIChat.styles';
import { getAppCopy } from '../appCopy';

// Safe Office.js bridge import — returns null when not inside OfficeProvider
let useOfficeSafe = () => ({ powerpoint: null });
try {
  const officeModule = require('../../../shared/services/office/OfficeContext');
  const _useOffice = officeModule.useOffice;
  useOfficeSafe = () => {
    try { return _useOffice(); } catch { return { powerpoint: null, bridge: null, app: null }; }
  };
} catch { /* Office module not available */ }

const debug = process.env.NODE_ENV === 'development' ? console.log : () => { };
const HOME_PROMPT_STORAGE_PREFIX = "restack_home_launch_payload";

const createWelcomeMessage = (app, workflows = []) => ({
  id: 'welcome-placeholder',
  sender: 'ai',
  timestamp: new Date().toISOString(),
  content: '', // No title text
  components: [
    {
      type: 'welcome_cards',
      data: {
        // App-aware heading + starters (see appCopy.js). Tapping a card fills the
        // composer (does not auto-send) so the user can add specifics like
        // "...for Acme Corp" before sending. The live host catalog replaces
        // these fallbacks once it loads.
        app: app || 'powerpoint',
        heading: getAppCopy(app).welcomeHeading,
        description: getAppCopy(app).welcomeDescription,
        cards: workflows.length > 0
          ? [...workflows]
            .sort(() => Math.random() - 0.5)
            .slice(0, 4)
            .map((workflow) => ({
              id: workflow.name,
              title: workflow.title || workflow.name,
              prompt: `/${workflow.name} `,
            }))
          : getAppCopy(app).starters
      },
      timestamp: new Date().toISOString()
    }
  ],
  isStreaming: false
});

const AIChat = ({
  collapsed,
  width,
  position = 'right',
  onToggleCollapse,
  toggleChatPosition,
  onResizeStart,
  onLogoClick,
  readOnly = false,
  showcaseIntroActive = false,
  showcasePrompt = null,
  onShowcaseReveal = null
}) => {
  const theme = useTheme();
  const tokens = theme.restack?.tokens || {};
  const chatRadii = getChatRadii(theme);
  const chatBorders = getChatBorders(theme);
  const { currentUser } = useAuth();

  // Transient "Signed in as <email>" confirmation pill. SSO sign-in is so
  // seamless that some users don't realize they're already authenticated —
  // this surfaces which account they're in, then auto-dismisses. Shown once
  // per mount when an email is available.
  const [showSignedInPill, setShowSignedInPill] = useState(false);
  const signedInPillShownRef = useRef(false);
  useEffect(() => {
    const email = currentUser?.email;
    if (email && !signedInPillShownRef.current) {
      signedInPillShownRef.current = true;
      setShowSignedInPill(true);
      const t = setTimeout(() => setShowSignedInPill(false), 8000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [currentUser?.email]);
  const {
    currentMessages,
    addMessage,
    addMessageToChat,
    applyServerTranscriptMode,
    clearCurrentChatMessages,
    createNewChat,
    currentChatId,
    chats,
    switchToChat,
    updateChatMetadata,
    presentationId,
    scopedTranscriptState
  } = usePresentationChats();
  // The active Office host bridge (PowerPoint or Excel) and which app we are in.
  // `bridge` is the generic active bridge; both bridges expose the same
  // executeCode / getInitialState / getUserContext surface, so downstream code
  // is host-agnostic. Default app to 'powerpoint' for the web (non-Office) shell.
  const { bridge: powerpointBridge, app: officeApp } = useOfficeSafe();
  const appCopy = useMemo(() => getAppCopy(officeApp), [officeApp]);

  // Construct currentPresentation for streaming hook (derived from auth, no context needed)
  const currentPresentation = useMemo(() => {
    return presentationId ? { id: presentationId, title: getAppCopy(officeApp).pluginLabel } : null;
  }, [presentationId, officeApp]);

  // Derive the active chat's title for the header (same logic as sidebar)
  const currentChatTitle = useMemo(() => {
    if (!currentChatId || !Array.isArray(chats)) return null;
    const active = chats.find((c) => c.id === currentChatId);
    if (!active) return null;
    const hasGeneratedTitle = active.name && active.name !== 'New chat';
    if (hasGeneratedTitle) return active.name;
    const firstUserMessage = (active.messages || []).find((m) => m.sender === 'user');
    return firstUserMessage?.content || active.name || null;
  }, [chats, currentChatId]);

  // State for history sidebar
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);
  // Presentation filename for sidebar display
  const [presentationTitle, setPresentationTitle] = useState(null);
  // State for "New Chat with Context" feature
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);
  // Track when a new chat was just created (for visual feedback)
  const [showNewChatIndicator, setShowNewChatIndicator] = useState(false);
  // Track chat switches for animation
  const [chatSwitchAnimation, setChatSwitchAnimation] = useState(false);
  const prevChatIdRef = useRef(currentChatId);
  const prevShowcaseIntroActiveRef = useRef(showcaseIntroActive);
  const repository = presentationRepository;
  const location = useLocation();
  const homeLaunchHandledRef = useRef(false);

  const [selectedModel, setSelectedModel] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(MODEL_STORAGE_KEY);
      return stored || DEFAULT_MODEL;
    }
    return DEFAULT_MODEL;
  });

  const composer = useChatComposer();
  const {
    setAttachmentsFromFiles: setComposerAttachments,
    setInput: setComposerInput,
    applyTextareaAutoSize,
    textareaRef
  } = composer;
  // Pre-fill composer with showcase prompt when in intro mode
  useEffect(() => {
    if (showcaseIntroActive && showcasePrompt && setComposerInput) {
      setComposerInput(showcasePrompt);
      // Trigger textarea auto-size after setting input
      if (applyTextareaAutoSize && textareaRef?.current) {
        setTimeout(() => applyTextareaAutoSize(textareaRef.current), 0);
      }
    }
  }, [showcaseIntroActive, showcasePrompt, setComposerInput, applyTextareaAutoSize, textareaRef]);

  // Clear composer when showcase intro ends (user clicked Send in overlay)
  useEffect(() => {
    if (prevShowcaseIntroActiveRef.current && !showcaseIntroActive) {
      // Showcase just ended - clear the pre-filled prompt from composer
      setComposerInput('');
      // Reset textarea height after clearing
      if (applyTextareaAutoSize && textareaRef?.current) {
        setTimeout(() => applyTextareaAutoSize(textareaRef.current), 0);
      }
    }
    prevShowcaseIntroActiveRef.current = showcaseIntroActive;
  }, [showcaseIntroActive, setComposerInput, applyTextareaAutoSize, textareaRef]);



  // Detect chat switches and trigger animation
  useEffect(() => {
    if (currentChatId && prevChatIdRef.current !== currentChatId) {
      setChatSwitchAnimation(true);
      prevChatIdRef.current = currentChatId;
      // Reset animation state after animation completes
      const timer = setTimeout(() => setChatSwitchAnimation(false), 400);
      return () => clearTimeout(timer);
    }
  }, [currentChatId]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(MODEL_STORAGE_KEY, selectedModel);
      } catch (storageError) {
        console.warn('Failed to persist model preference:', storageError);
      }
    }
  }, [selectedModel]);

  const handleModelChange = useCallback((newModel) => {
    setSelectedModel(newModel);
  }, []);

  const chatContext = {
    currentMessages,
    addMessage,
    addMessageToChat,
    applyServerTranscriptMode,
    clearCurrentChatMessages,
    currentChatId,
    chats,
    switchToChat,
    updateChatMetadata,
    scopedTranscriptState
  };

  // Slide selection tracking
  const [selectedSlides, setSelectedSlides] = useState([]);

  const streaming = useStreamingSession({
    currentUser,
    currentPresentation,
    repository,
    selectedModel,
    selectedSlides,
    analytics,
    chatData: chatContext,
    composer,
    powerpointBridge,
    app: officeApp || 'powerpoint'
  });

  const {
    handleSend: streamingHandleSend,
    handleStopRequest: streamingHandleStopRequest,
    retryMessage: streamingRetryMessage,
    messagesContainerRef,
    messagesEndRef,
    pendingMessage,
    isLoading,
    pendingUserQuestion,
    answerUserQuestion,
    pendingProposeEdits,
    submitProposeEdits,
    applyProposeEdit,
    compactChat,
    canCompactChat,
    cancelPeerWait,
    conductorOnline,
    conductorPeers,
    connectConductor,
    awaitingPeerLabel,
    ephemeralMessages,
    documentPrivacy,
    liveDataAvailable,
    liveDataMode,
    onLiveDataModeChange,
    liveDataSession,
    officeOperationRecoveryRequired,
    officeOperationRecovery,
    resolveOfficeOperationRecovery,
    dismissOfficeOperationRecovery,
    privateSessionActive,
  } = streaming;
  const [
    officeResolutionError,
    setOfficeResolutionError,
  ] = useState(null);
  const [
    officeResolutionPending,
    setOfficeResolutionPending,
  ] = useState(false);
  // Which action is awaiting a second click: null | 'resolve' | 'dismiss'.
  //
  // This used to be window.confirm(). An Office task pane suppresses the native
  // dialogs, so confirm() returned falsy, the `if (!confirmed) return` fired,
  // and BOTH buttons became silent no-ops: the banner could not be resolved or
  // dismissed from the pane at all, only by deleting local storage from dev
  // tools. These were the only two window.confirm calls in the chat surface.
  // Confirming inline keeps the deliberate second step without depending on a
  // host dialog that is not guaranteed to exist.
  const [officeConfirmAction, setOfficeConfirmAction] = useState(null);
  useEffect(() => {
    setOfficeResolutionError(null);
    setOfficeConfirmAction(null);
  }, [officeOperationRecovery?.operationId]);
  const handleResolveOfficeOperation = useCallback(async () => {
    if (
      !officeOperationRecovery?.operationId
      || typeof resolveOfficeOperationRecovery !== 'function'
    ) {
      return;
    }
    if (officeConfirmAction !== 'resolve') {
      setOfficeConfirmAction('resolve');
      return;
    }
    setOfficeConfirmAction(null);
    setOfficeResolutionPending(true);
    setOfficeResolutionError(null);
    try {
      await resolveOfficeOperationRecovery({
        operationId: officeOperationRecovery.operationId,
        confirmReviewed: true,
      });
    } catch (error) {
      setOfficeResolutionError(
        error?.message
        || 'The exact Office operation could not be resolved.'
      );
    } finally {
      setOfficeResolutionPending(false);
    }
  }, [
    officeConfirmAction,
    officeOperationRecovery?.operationId,
    resolveOfficeOperationRecovery,
  ]);
  const handleDismissOfficeOperation = useCallback(() => {
    if (typeof dismissOfficeOperationRecovery !== 'function') return;
    if (officeConfirmAction !== 'dismiss') {
      setOfficeConfirmAction('dismiss');
      return;
    }
    setOfficeConfirmAction(null);
    setOfficeResolutionError(null);
    dismissOfficeOperationRecovery({
      operationId: officeOperationRecovery?.operationId || null,
    });
  }, [
    dismissOfficeOperationRecovery,
    officeConfirmAction,
    officeOperationRecovery?.operationId,
  ]);
  const cancelOfficeConfirmAction = useCallback(() => {
    setOfficeConfirmAction(null);
  }, []);

  // Workflow list for the composer's slash-command menu (bundled + the
  // user's custom workflows). Loaded once per mount; the sidebar panel keeps
  // its own live copy for add/delete.
  const [slashWorkflows, setSlashWorkflows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { fetchWorkflowIndex } = await import('./components/SidebarPanels');
        const { bundled, custom } = await fetchWorkflowIndex(currentUser, officeApp || 'powerpoint');
        if (!cancelled) setSlashWorkflows([...bundled, ...custom]);
      } catch (e) { /* slash menu just stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [currentUser, officeApp]);

  // Pin/unpin a chat in the history sidebar (persisted on the chat doc;
  // pinned chats sort first in usePresentationChats).
  const handleTogglePin = useCallback(async (chatId, pinned) => {
    if (!currentPresentation?.id || !chatId) return;
    try {
      await repository.updateChat(currentPresentation.id, chatId, { pinned });
    } catch (e) {
      console.warn('Failed to toggle chat pin:', e?.message);
    }
  }, [currentPresentation?.id, repository]);

  useEffect(() => {
    if (!powerpointBridge || officeApp !== 'powerpoint') return;
    let inFlight = false;
    const pollInterval = setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const ctx = await powerpointBridge.getUserContext();
        setSelectedSlides(ctx?.selectedSlides || []);
      } catch { /* ignore */ } finally {
        inFlight = false;
      }
    }, 2500);
    return () => clearInterval(pollInterval);
  }, [powerpointBridge, officeApp]);

  // Fetch presentation filename on mount
  useEffect(() => {
    if (!powerpointBridge) return;
    (async () => {
      try {
        const state = await powerpointBridge.getInitialState();
        if (state?.fileName) setPresentationTitle(state.fileName);
      } catch { /* ignore */ }
    })();
  }, [powerpointBridge]);

  // Handle suggestion card click — populate input and focus
  const handleSuggestionClick = useCallback((cardTitle) => {
    if (setComposerInput) {
      setComposerInput(cardTitle);
    }
    if (textareaRef?.current) {
      textareaRef.current.focus();
      if (applyTextareaAutoSize) {
        setTimeout(() => applyTextareaAutoSize(textareaRef.current), 0);
      }
    }
  }, [setComposerInput, textareaRef, applyTextareaAutoSize]);

  // Scroll to top when showcase mode is active (so user message is visible at top-right)
  useEffect(() => {
    if (showcaseIntroActive && messagesContainerRef?.current) {
      // Use double requestAnimationFrame to ensure content is fully rendered before scrolling
      // First RAF schedules after paint, second ensures layout is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = 0;
          }
        });
      });
    }
  }, [showcaseIntroActive, messagesContainerRef]);

  const sendMessage = useCallback((messageOverride = null, options = {}) => {
    // Handle showcase intro mode - reveal instead of sending
    if (showcaseIntroActive && onShowcaseReveal) {
      setComposerInput(''); // Clear the pre-filled prompt
      onShowcaseReveal();
      return;
    }
    if (readOnly || scopedTranscriptState === 'locked') {
      return;
    }
    return streamingHandleSend(messageOverride, options);
  }, [
    onShowcaseReveal,
    readOnly,
    scopedTranscriptState,
    setComposerInput,
    showcaseIntroActive,
    streamingHandleSend,
  ]);

  const stopRequest = useCallback(() => {
    if (readOnly) {
      return;
    }
    return streamingHandleStopRequest();
  }, [readOnly, streamingHandleStopRequest]);

  const retryMessage = useCallback((originalMessage) => {
    if (readOnly) {
      return;
    }
    return streamingRetryMessage(originalMessage);
  }, [readOnly, streamingRetryMessage]);

  // Clarification UI was removed along with plan mode. The streamProcessor
  // no longer handles `clarification` events, and the backend doesn't emit
  // them. The answeredClarificationIds / respondingClarificationIds state
  // and handleClarificationSelect callback were orphaned — deleted here.

  const hydrateSerializedFiles = useCallback(async (serializedFiles = []) => {
    if (typeof window === 'undefined' || typeof File === 'undefined') {
      return [];
    }

    if (!Array.isArray(serializedFiles) || serializedFiles.length === 0) {
      return [];
    }

    const buildFileFromDataUrl = async (fileMeta) => {
      if (!fileMeta || typeof fileMeta.dataUrl !== 'string' || !fileMeta.dataUrl) {
        return null;
      }

      try {
        const response = await fetch(fileMeta.dataUrl);
        const blob = await response.blob();
        const fileName = fileMeta.name || 'attachment';
        const fileType = fileMeta.type || blob.type || 'application/octet-stream';
        const fileOptions = { type: fileType };
        if (fileMeta.lastModified) {
          fileOptions.lastModified = fileMeta.lastModified;
        }
        return new File([blob], fileName, fileOptions);
      } catch (error) {
        console.error('Failed to hydrate attachment from home prompt payload:', error);
        return null;
      }
    };

    const hydratedFiles = await Promise.all(serializedFiles.map(buildFileFromDataUrl));
    return hydratedFiles.filter(Boolean);
  }, []);

  useEffect(() => {
    if (homeLaunchHandledRef.current) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const search = location?.search || '';
    if (!search) {
      return;
    }

    const params = new URLSearchParams(search);
    if (params.get('launch') !== 'home') {
      return;
    }

    if (!currentPresentation?.id) {
      return;
    }

    const storageKey = `${HOME_PROMPT_STORAGE_PREFIX}_${currentPresentation.id}`;
    let storedPayload;

    try {
      storedPayload = window.sessionStorage.getItem(storageKey);
      if (!storedPayload) {
        return;
      }
    } catch (error) {
      console.error('Failed to access home prompt payload from storage:', error);
      return;
    }

    const bootstrap = async () => {
      homeLaunchHandledRef.current = true;

      try {
        const payload = JSON.parse(storedPayload) || {};
        const serializedAttachments = Array.isArray(payload.attachments) ? payload.attachments : [];
        const hydratedFiles = await hydrateSerializedFiles(serializedAttachments);

        if (hydratedFiles.length > 0 && typeof setComposerAttachments === 'function') {
          setComposerAttachments(hydratedFiles);
        }

        if (payload.prompt && typeof setComposerInput === 'function') {
          setComposerInput(payload.prompt);
        }

        if (typeof applyTextareaAutoSize === 'function' && textareaRef?.current) {
          applyTextareaAutoSize(textareaRef.current);
        }

        await new Promise((resolve) => window.setTimeout(resolve, 0));

        await sendMessage(payload.prompt || null);
      } catch (error) {
        console.error('Failed to bootstrap AI chat from home prompt payload:', error);
      } finally {
        try {
          window.sessionStorage.removeItem(storageKey);
        } catch (storageError) {
          console.warn('Failed to clear home prompt payload from storage:', storageError);
        }
      }
    };

    bootstrap();
  }, [
    applyTextareaAutoSize,
    currentPresentation?.id,
    hydrateSerializedFiles,
    location.search,
    sendMessage,
    setComposerAttachments,
    setComposerInput,
    textareaRef,
  ]);

  // Check if current chat has any user messages (determines if New Chat button is enabled)
  const hasUserMessages = currentMessages.some(msg => msg.sender === 'user') ||
    (pendingMessage && pendingMessage.sender === 'user');

  /**
   * Handle "New Chat" - Creates a new, independent chat thread
   *
   * The old chat is preserved (user can switch back via History).
   */
  const handleStartNewChat = useCallback(async () => {
    if (readOnly || isCreatingNewChat) {
      return;
    }

    try {
      setIsCreatingNewChat(true);
      debug('🔄 Creating new chat...');

      // Create a fresh new chat (no context inheritance)
      const result = await createNewChat(null, { origin: officeApp || 'powerpoint' });

      if (result) {
        // Get the new chat that was created
        const newChat = result?.chats?.[result.chats.length - 1];

        if (newChat) {
          // Explicitly switch to the new chat
          switchToChat(newChat.id);
          setShowHistorySidebar(false);
          debug('🔀 Switched to new chat:', newChat.id);

          // Show "New chat" indicator briefly
          setShowNewChatIndicator(true);
          setTimeout(() => setShowNewChatIndicator(false), 3000);

          debug('✅ New chat created:', newChat.id);
        }
      }
    } catch (error) {
      console.error('❌ Failed to create new chat:', error);
    } finally {
      setIsCreatingNewChat(false);
    }
  }, [readOnly, isCreatingNewChat, createNewChat, switchToChat, officeApp]);

  const handleArchiveChat = useCallback(async (chatId, archived) => {
    if (!currentPresentation?.id || !chatId) return;
    try {
      await repository.updateChat(currentPresentation.id, chatId, {
        archived,
        ...(archived ? { pinned: false } : {}),
      });
      if (archived && chatId === currentChatId) {
        const replacement = (chats || []).find((chat) => chat.id !== chatId && chat.archived !== true);
        if (replacement) switchToChat(replacement.id);
        else await handleStartNewChat();
      }
    } catch (error) {
      console.warn('Failed to archive chat:', error?.message);
    }
  }, [currentPresentation?.id, currentChatId, chats, repository, switchToChat, handleStartNewChat]);

  // An injected cross-app (Conductor) turn produces AI messages with no local
  // user message, so gate the welcome cards on ANY visible chat activity, not
  // just user messages. Otherwise a peer-driven pane does its work invisibly
  // behind the welcome screen and looks dead to the user.
  const isUserVisibleMessage = useCallback(
    (message) => !message.hidden && !message.metadata?.peerStatus,
    []
  );
  const visibleEphemeralMessages = useMemo(
    () => (ephemeralMessages || []).filter(isUserVisibleMessage),
    [ephemeralMessages, isUserVisibleMessage]
  );
  const hasVisibleActivity =
    currentMessages.some(isUserVisibleMessage) ||
    visibleEphemeralMessages.length > 0 ||
    Boolean(pendingMessage) ||
    scopedTranscriptState === 'locked' ||
    scopedTranscriptState === 'loading';
  const shouldShowWelcomeCards = !hasUserMessages && !hasVisibleActivity;

  const welcomeMessage = useMemo(
    () => createWelcomeMessage(officeApp, slashWorkflows),
    [officeApp, slashWorkflows]
  );
  const welcomeCardData = welcomeMessage?.components?.[0]?.data;

  const visibleMessages = useMemo(
    () => [
      ...currentMessages.filter(isUserVisibleMessage),
      ...visibleEphemeralMessages,
    ],
    [currentMessages, isUserVisibleMessage, visibleEphemeralMessages]
  );

  const liveDataBanner = useMemo(() => {
    if (liveDataSession?.active) {
      return {
        message: liveDataSession.message || 'Live data is working in the open file',
        tone: 'info',
      };
    }
    if (liveDataSession?.stage === 'failed' || liveDataSession?.stage === 'cancelled') {
      return {
        message: liveDataSession.message || 'The live data request did not complete',
        tone: 'error',
      };
    }
    if (documentPrivacy?.state === 'licensed_data') {
      return {
        message: 'Live data document. New turns are not saved to chat history.',
        tone: 'info',
      };
    }
    if (liveDataMode) {
      return {
        message: 'Live data mode. This request writes to the open file and is not saved.',
        tone: 'info',
      };
    }
    return null;
  }, [documentPrivacy?.state, liveDataMode, liveDataSession]);

  // DIAGNOSTIC: Track pendingMessage vs currentMessages for research components
  useEffect(() => {
    if (pendingMessage && pendingMessage.sender === 'ai') {
      const pendingResearch = pendingMessage.components?.find(c => c.type === 'research');
      if (pendingResearch) {
        const inCurrentMessages = currentMessages.some(msg => msg.id === pendingMessage.id);
        const currentVersion = currentMessages.find(msg => msg.id === pendingMessage.id);
        const currentResearch = currentVersion?.components?.find(c => c.type === 'research');

        console.log('🔄 RENDER SOURCE DIAGNOSTIC:', {
          pendingMessageId: pendingMessage.id,
          isInCurrentMessages: inCurrentMessages,
          willRenderFrom: inCurrentMessages ? 'currentMessages (Firestore)' : 'pendingMessage (live)',
          pendingSearchQueriesCount: pendingResearch.data?.searchQueries?.length || 0,
          pendingSearchQueriesPreview: pendingResearch.data?.searchQueries?.slice(0, 2),
          currentSearchQueriesCount: currentResearch?.data?.searchQueries?.length || 0,
          currentSearchQueriesPreview: currentResearch?.data?.searchQueries?.slice(0, 2),
          searchQueriesMatch: (pendingResearch.data?.searchQueries?.length || 0) === (currentResearch?.data?.searchQueries?.length || 0)
        });
      }
    }
  }, [pendingMessage, currentMessages]);

  const lastAiMessageIndex = useMemo(() => {
    let lastIndex = -1;
    visibleMessages.forEach((message, index) => {
      if (message.sender === 'ai') {
        lastIndex = index;
      }
    });
    return lastIndex;
  }, [visibleMessages]);

  // WHICH REPLY THE RUNNING TURN IS WRITING INTO, IF ONE IS RUNNING.
  //
  // `isLoading` is the composer's Stop-instead-of-Send flag, and `pendingMessage`
  // is the live bubble the turn streams into, so together they name exactly one
  // message. MessageRenderer needs that pair to keep its elapsed count running
  // for a live turn whose message metadata already claims to be finished. It
  // has to be the pair and not `isLoading` alone: between sending and the new
  // reply appearing, the PREVIOUS reply is still the last AI message, and it is
  // finished.
  const liveTurnMessageId = isLoading ? (pendingMessage?.id ?? null) : null;


  if (collapsed) {
    return null;
  }

  return (
    <ChatSidebar collapsed={collapsed} width={width} position={position}>
      {onResizeStart && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            ...(position === 'right' ? { left: 0 } : { right: 0 }),
            transform: position === 'right' ? 'translateX(-50%)' : 'translateX(50%)',
            width: theme.spacing(tokens.spacing.nano + tokens.spacing.quark),
            height: '100%',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            transition: 'opacity 0.2s ease',
            zIndex: 10,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: '1px',
              transform: 'translateX(-50%)',
              backgroundColor: theme.palette.divider
            },
            '&:hover::after': {
              backgroundColor: theme.palette.primary.main,
              opacity: 0.8
            }
          }}
          onMouseDown={onResizeStart}
        />
      )}

      <ChatHeaderBar
        onNewChat={handleStartNewChat}
        isCreatingNewChat={isCreatingNewChat}
        canCreateNewChat={hasUserMessages}
        onOpenHistorySidebar={() => setShowHistorySidebar(true)}
        chatTitle={currentChatTitle}
        app={officeApp || 'powerpoint'}
        conductorOnline={conductorOnline}
        conductorPeers={conductorPeers}
        onConnectorClick={connectConductor}
      />

      {/* Signed-in confirmation pill — floats near the top of the chat,
          auto-dismisses after 8s. Gives feedback that (silent SSO) sign-in
          actually happened and shows which account. */}
      <Fade in={showSignedInPill && !!currentUser?.email} timeout={400} unmountOnExit>
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'absolute',
            top: theme.spacing(7.5),
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            maxWidth: 'calc(100% - 32px)',
            px: 1.5,
            py: 0.75,
            borderRadius: chatRadii.pill,
            // Match the welcome suggestion cards ("Summarize this deck", etc.):
            // the shared resting border, no shadow, near-transparent fill.
            // A faint paper wash keeps it legible when it floats over content
            // without reading as a heavy popover.
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.6)
              : alpha(theme.palette.background.paper, 0.85),
            border: chatBorders.resting,
            boxShadow: 'none',
            backdropFilter: 'blur(6px)',
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: chatRadii.circle,
              backgroundColor: theme.palette.success.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box component="span" sx={{ color: '#fff', fontSize: 9, lineHeight: 1, fontWeight: 700 }}>✓</Box>
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: theme.typography.pxToRem(11),
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Signed in as{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {currentUser?.email}
            </Box>
          </Typography>
        </Box>
      </Fade>

      {/* New Chat Indicator */}
      <Fade in={showNewChatIndicator} timeout={400} unmountOnExit>
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'relative',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            borderBottom: `${chatBorders.width}px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: chatRadii.circle,
              backgroundColor: theme.palette.success.main,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.success.main,
              fontWeight: 600,
              letterSpacing: '0.02em',
            }}
          >
            New chat started
          </Typography>
        </Box>
      </Fade>

      {liveDataBanner && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: `${chatBorders.width}px solid ${theme.palette.divider}`,
            backgroundColor: alpha(
              liveDataBanner.tone === 'error'
                ? theme.palette.error.main
                : theme.palette.info.main,
              theme.palette.mode === 'dark' ? 0.14 : 0.08
            ),
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: chatRadii.circle,
              flexShrink: 0,
              backgroundColor: liveDataBanner.tone === 'error'
                ? theme.palette.error.main
                : theme.palette.info.main,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: liveDataBanner.tone === 'error'
                ? theme.palette.error.main
                : theme.palette.info.main,
              fontWeight: 600,
            }}
          >
            {liveDataBanner.message}
          </Typography>
        </Box>
      )}

      {officeOperationRecoveryRequired && (
        <Box
          role="alert"
          sx={{
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            borderBottom: `${chatBorders.width}px solid ${theme.palette.divider}`,
            backgroundColor: alpha(
              theme.palette.warning.main,
              theme.palette.mode === 'dark' ? 0.14 : 0.08
            ),
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: chatRadii.circle,
              flexShrink: 0,
              backgroundColor: theme.palette.warning.main,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.warning.main,
                fontWeight: 600,
                display: 'block',
              }}
            >
              {!officeOperationRecovery?.operationId
                ? 'Secure Office operation recovery is unavailable in this pane. Review the open file before continuing. You can dismiss this notice.'
                : officeOperationRecovery.serverConfirmedPending
                  ? `Operation ${officeOperationRecovery.operationId.slice(-8)} is still running on the server. Review the open file, then resolve it.`
                  : officeOperationRecovery.reconcileExhausted
                    ? `Operation ${officeOperationRecovery.operationId.slice(-8)} could not be confirmed with the server. Review the open file, then resolve or dismiss it.`
                    : `A secure Office operation has an unknown outcome. Review the open file before resolving operation ${officeOperationRecovery.operationId.slice(-8)}.`}
            </Typography>
            {officeResolutionError && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  display: 'block',
                  mt: 0.5,
                }}
              >
                {officeResolutionError}
              </Typography>
            )}
            {officeConfirmAction && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  display: 'block',
                  mt: 0.5,
                }}
              >
                {officeConfirmAction === 'resolve'
                  ? 'Review the open Office file first. Continue only if you have checked whether the prior operation changed the file. This clears only this operation and will not run it again.'
                  : officeOperationRecovery?.serverConfirmedPending
                    ? 'The server still reports this operation as running, so this hides the notice without clearing it. Use Resolve once you have reviewed the file.'
                    : 'This clears a local record of an operation the server has no trace of. It does not undo or re-run anything in your file.'}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
              {officeOperationRecovery?.operationId
                && officeConfirmAction !== 'dismiss' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={officeResolutionPending}
                  onClick={handleResolveOfficeOperation}
                >
                  {officeResolutionPending
                    ? 'Resolving...'
                    : officeConfirmAction === 'resolve'
                      ? 'Confirm resolve'
                      : 'I reviewed the file. Resolve'}
                </Button>
              )}
              {/* Always reachable: the previous banner could only be cleared
                  by deleting local storage from dev tools. */}
              {officeConfirmAction !== 'resolve' && (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  disabled={officeResolutionPending}
                  onClick={handleDismissOfficeOperation}
                >
                  {officeConfirmAction === 'dismiss' ? 'Confirm dismiss' : 'Dismiss'}
                </Button>
              )}
              {officeConfirmAction && (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  disabled={officeResolutionPending}
                  onClick={cancelOfficeConfirmAction}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <ChatMessages
        ref={messagesContainerRef}
        isWelcomeView={shouldShowWelcomeCards}
        sx={{
          ...(chatSwitchAnimation && {
            animation: 'chatFadeIn 0.35s ease-out',
            '@keyframes chatFadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(8px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }),
        }}
      >
        {scopedTranscriptState === 'locked' ? (
          <Box
            role="alert"
            sx={{
              width: '100%',
              color: 'text.secondary',
              textAlign: 'center',
              py: 3,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Secure chat unavailable
            </Typography>
            <Typography variant="caption">
              Restore access to the Knowledge Locker scope or start a new chat.
            </Typography>
          </Box>
        ) : shouldShowWelcomeCards ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <WelcomeCards data={welcomeCardData} onSuggestionClick={handleSuggestionClick} />
          </Box>
        ) : (
          <>
            {visibleMessages.map((message, index) => {
              const isLatestAIMessage = message.sender === 'ai' && index === lastAiMessageIndex;

              return (
                <React.Fragment key={message.id}>
                <ChatMessageCard
                    message={message}
                    isStreaming={Boolean(message.isStreaming)}
                  >
                    <MessageRenderer
                      message={message}
                      theme={theme}
                      onRetryMessage={retryMessage}
                      isLatestAIMessage={isLatestAIMessage}
                      isTurnLive={Boolean(liveTurnMessageId) && message.id === liveTurnMessageId}
                    />
                  </ChatMessageCard>

                </React.Fragment>
              );
            })}

            {pendingMessage && !visibleMessages.some(msg => msg.id === pendingMessage.id) && (
              <ChatMessageCard
                key={pendingMessage.id}
                message={pendingMessage}
                isStreaming={Boolean(pendingMessage.isStreaming)}
              >
                <MessageRenderer
                  message={pendingMessage}
                  theme={theme}
                  onRetryMessage={retryMessage}
                  isLatestAIMessage={pendingMessage.sender === 'ai'}
                  isTurnLive={Boolean(liveTurnMessageId) && pendingMessage.id === liveTurnMessageId}
                />
              </ChatMessageCard>
            )}
          </>
        )}

        <div ref={messagesEndRef} />
      </ChatMessages>

      {/* Org seat claim — when a signed-in member's work email matches a
          pending invite or an admin-enabled domain, one click seats them.
          Members claim their seat here, in the plugin, and never visit the
          console. */}
      <OrgSeatBanner />

      {/* Sandbox activation card — shown above the composer for free-plan
          users with zero credits. Credit balance visualization lives only
          in the history sidebar footer (the main chat shouldn't double up
          on credit displays). */}
      <SandboxRedemptionBanner />

      {/* ask_user_question options card — the agent is paused on this
          tool call until the user answers (or the turn ends). */}
      {pendingUserQuestion && (
        <UserQuestionCard
          request={pendingUserQuestion}
          onSubmit={answerUserQuestion}
        />
      )}

      {/* propose_doc_edits review cards (Word): the agent is paused on this
          tool call until the user works through each staged edit (or the turn ends). */}
      {pendingProposeEdits && (
        <ProposeEditsCard
          request={pendingProposeEdits}
          onApply={applyProposeEdit}
          onSubmit={submitProposeEdits}
        />
      )}

      <ChatComposerWrapper>
        <ChatComposer
          composer={composer}
          isLoading={isLoading}
          onSend={sendMessage}
          onStopRequest={stopRequest}
          readOnly={
            readOnly
            || scopedTranscriptState === 'locked'
            || officeOperationRecoveryRequired
          }
          placeholder={
            officeOperationRecoveryRequired
              ? 'Review the prior Office operation before continuing'
              : scopedTranscriptState === 'locked'
              ? 'Secure chat access is unavailable'
              : appCopy.composerPlaceholder
          }
          selectedModel={selectedModel}
          onModelChange={handleModelChange}
          awaitingPeerLabel={awaitingPeerLabel}
          slashCommands={slashWorkflows}
          onCompactChat={canCompactChat ? compactChat : null}
          onCancelPeerWait={cancelPeerWait}
          liveDataAvailable={liveDataAvailable}
          liveDataMode={liveDataMode}
          onLiveDataModeChange={onLiveDataModeChange}
          privateSessionActive={privateSessionActive}
        />
      </ChatComposerWrapper>

      <HistorySidebarPanel
        open={showHistorySidebar}
        onClose={() => setShowHistorySidebar(false)}
        chats={chats}
        currentChatId={currentChatId}
        switchToChat={switchToChat}
        onNewChat={handleStartNewChat}
        onSuggestionClick={handleSuggestionClick}
        isCreatingNewChat={isCreatingNewChat}
        canCreateNewChat={hasUserMessages}
        presentationTitle={presentationTitle}
        onTogglePin={handleTogglePin}
        onArchiveChat={handleArchiveChat}
        app={officeApp || 'powerpoint'}
      />
    </ChatSidebar>
  );
};

export default AIChat;
